/* Structured answer blocks for the Ask Revvly chat — metric tiles, bar chart,
   table, preview card, callout, action row, and inline choice picker. Ported
   from the design system (hifi/rv-chat.css) to canonical --rv-* tokens.
   Note: this app's dark mode does NOT remap --rv-neutral-*, so surface fills
   use --rv-surface / --rv-bg-app-subtle (mode-aware), never --rv-neutral-*. */

.Block {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-2);
}

.BlockLabel {
  font-family: var(--rv-font-sans);
  font-size: var(--rv-text-xxs);
  font-weight: var(--rv-w-semibold);
  letter-spacing: var(--rv-track-widest);
  text-transform: uppercase;
  color: var(--rv-fg-4);
}

/* ---------------- metric tiles ---------------- */

.Metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: var(--rv-space-2);
}

.Metric {
  padding: var(--rv-space-3);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-xl);
  background: var(--rv-surface);
}

.MetricLabel {
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-3);
  margin-bottom: var(--rv-space-2);
}

.MetricValue {
  font-family: var(--rv-font-sans);
  font-weight: var(--rv-w-bold);
  font-size: var(--rv-text-xl);
  letter-spacing: var(--rv-track-tight);
  line-height: 1;
  color: var(--rv-fg-1);
  font-variant-numeric: tabular-nums;
}

.Delta {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xxs);
  margin-top: var(--rv-space-2);
  padding: var(--space-xxs) var(--rv-space-2);
  border-radius: var(--rv-radius-sm);
  font-size: var(--rv-text-xs);
  font-weight: var(--rv-w-medium);
}

.Delta svg {
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
}

.DeltaUp {
  background: var(--rv-success-bg);
  color: var(--rv-success-ink);
}

.DeltaDown {
  background: var(--rv-danger-bg);
  color: var(--rv-danger-ink);
}

/* ---------------- bar chart ---------------- */

.Chart {
  --ah-chart-height: 8rem;
  --ah-bar-max: 2.25rem;

  padding: var(--rv-space-4);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-xl);
  background: var(--rv-surface);
}

.Bars {
  display: flex;
  align-items: flex-end;
  gap: var(--rv-space-3);
  height: var(--ah-chart-height);
}

.BarCol {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: var(--rv-space-2);
  height: 100%;
}

.Bar {
  width: 100%;
  max-width: var(--ah-bar-max);
  height: var(--ah-bar, 20%);
  border-radius: var(--rv-radius-sm) var(--rv-radius-sm) var(--border-radius-xs)
    var(--border-radius-xs);
  background: var(--rv-neutral-200);
  transition: height var(--rv-dur-slow) var(--rv-ease-out);
}

.BarHi {
  background: var(--rv-accent);
}

.BarDown {
  background: var(--rv-danger);
}

.BarLabel {
  font-size: var(--rv-text-xxs);
  color: var(--rv-fg-4);
  font-variant-numeric: tabular-nums;
}

/* ---------------- sources (help articles) ---------------- */

.Sources {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-xl);
  background: var(--rv-surface);
  overflow: hidden;
}

.Source {
  display: flex;
  align-items: center;
  gap: var(--rv-space-2);
  padding: var(--rv-space-2) var(--rv-space-3);
  font-size: var(--rv-text-sm);
}

.Source + .Source {
  border-top: 1px solid var(--rv-border);
}

.SourceIcon {
  flex-shrink: 0;
  width: var(--rv-icon-sm);
  height: var(--rv-icon-sm);
  color: var(--rv-fg-4);
}

.SourceTitle {
  display: inline-flex;
  flex: 1;
  align-items: center;
  gap: var(--rv-space-1);
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--rv-fg-1);
}

a.SourceTitle {
  text-decoration: none;
}

a.SourceTitle:hover {
  text-decoration: underline;
}

.SourceExternal {
  flex-shrink: 0;
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
  color: var(--rv-fg-4);
}

.SourceAudience {
  flex-shrink: 0;
}

/* ---------------- table ---------------- */

.TableWrap {
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-xl);
  overflow: hidden;
  background: var(--rv-surface);
}

.Table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--rv-text-sm);
}

.Table th {
  text-align: left;
  font-family: var(--rv-font-sans);
  font-weight: var(--rv-w-semibold);
  font-size: var(--rv-text-xxs);
  letter-spacing: var(--rv-track-wide);
  text-transform: uppercase;
  color: var(--rv-fg-3);
  padding: var(--rv-space-2) var(--rv-space-3);
  background: var(--rv-bg-app-subtle);
  border-bottom: 1px solid var(--rv-border);
}

/* A numeric column's header must right-align to sit over its (right-aligned)
   values — otherwise `.Table th` (higher specificity) leaves it left of them. */
.Table th.Numeric {
  text-align: right;
}

.Table td {
  padding: var(--rv-space-3);
  border-bottom: 1px solid var(--rv-border-subtle);
  color: var(--rv-fg-1);
  vertical-align: middle;
}

.Table tr:last-child td {
  border-bottom: none;
}

.Numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* ---------------- preview / draft card ---------------- */

.Preview {
  border: 1px solid var(--rv-accent);
  border-radius: var(--rv-radius-2xl);
  overflow: hidden;
  background: var(--rv-surface);
  box-shadow:
    0 0 0 1px var(--rv-accent-glow-12),
    var(--rv-shadow-sm);
}

.PreviewHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--rv-space-2);
  padding: var(--rv-space-3) var(--rv-space-4);
  border-bottom: 1px solid var(--rv-border);
}

.PreviewTitle {
  display: flex;
  align-items: center;
  gap: var(--rv-space-2);
  font-family: var(--rv-font-display);
  font-weight: var(--rv-w-medium);
  font-size: var(--rv-text-md);
  color: var(--rv-fg-1);
}

.PreviewTitle svg {
  width: var(--rv-icon-sm);
  height: var(--rv-icon-sm);
  color: var(--rv-accent);
}

.PreviewTag {
  font-family: var(--rv-font-mono);
  font-size: var(--rv-text-xxs);
  color: var(--rv-fg-3);
}

.PreviewBody {
  padding: var(--rv-space-4);
}

.Fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--rv-space-2) var(--rv-space-5);
  font-size: var(--rv-text-sm);
}

.Field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--rv-space-3);
  padding-bottom: var(--rv-space-2);
  border-bottom: 1px dashed var(--rv-border-subtle);
}

.FieldKey {
  color: var(--rv-fg-3);
}

.FieldValue {
  color: var(--rv-fg-1);
  font-weight: var(--rv-w-medium);
}

.PreviewNote {
  margin-top: var(--rv-space-3);
}

/* ---------------- callout ---------------- */

.Callout {
  display: flex;
  gap: var(--rv-space-2);
  padding: var(--rv-space-3);
  border-radius: var(--rv-radius-md);
  font-size: var(--rv-text-sm);
  line-height: var(--line-height-lg);
  background: var(--rv-bg-app-subtle);
  border: 1px solid var(--rv-border);
  color: var(--rv-fg-2);
}

.Callout svg {
  width: var(--rv-icon-sm);
  height: var(--rv-icon-sm);
  flex-shrink: 0;
  margin-top: var(--space-xxs);
}

.CalloutAccent {
  background: var(--rv-accent-glow-12);
  border-color: transparent;
  box-shadow: inset 0 0 0 1px var(--rv-accent-glow-20);
  color: var(--content-foundation-accent-a);
}

.CalloutWarn {
  background: var(--rv-warning-bg);
  border-color: transparent;
  color: var(--rv-warning-ink);
}

.CalloutDanger {
  background: var(--rv-danger-bg);
  border-color: transparent;
  color: var(--rv-danger-ink);
}

/* ---------------- action row ---------------- */

.Actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--rv-space-2);
}

.ActionLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xxs);
  padding: var(--rv-space-2) var(--rv-space-3);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-md);
  background: var(--rv-surface);
  color: var(--rv-fg-1);
  font-size: var(--rv-text-sm);
  font-weight: var(--rv-w-medium);
  text-decoration: none;
}

.ActionLink:hover {
  background: var(--rv-bg-app-subtle);
}

.ActionLinkPrimary {
  background: var(--rv-accent);
  border-color: var(--rv-accent);
  color: var(--rv-accent-ink-deep);
}

.ActionLinkPrimary:hover {
  background: var(--rv-accent-hover);
}

/* ---------------- inline choice picker ---------------- */

.Choice {
  padding: var(--rv-space-3) var(--rv-space-4);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-xl);
  background: var(--rv-bg-app-subtle);
}

.ChoiceDone {
  border-style: dashed;
  background: transparent;
}

.ChoiceQuestion {
  display: flex;
  align-items: center;
  gap: var(--rv-space-2);
  margin-bottom: var(--rv-space-3);
  font-size: var(--rv-text-sm);
  font-weight: var(--rv-w-medium);
  color: var(--rv-fg-1);
}

.ChoiceQuestion svg {
  width: var(--rv-icon-sm);
  height: var(--rv-icon-sm);
  color: var(--rv-accent);
}

/* A question the conversation has moved past: no longer the one to answer. */
.ChoiceQuestionDone {
  margin-bottom: 0;
  font-weight: var(--rv-w-regular);
  color: var(--rv-fg-3);
}

.ChoiceQuestionDone svg {
  color: var(--rv-fg-3);
}

.ChoiceHint {
  margin: var(--rv-space-3) 0 0;
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-3);
}

.ChoicePills {
  display: flex;
  flex-wrap: wrap;
  gap: var(--rv-space-2);
}

.ChoiceCards {
  display: flex;
  flex-wrap: wrap;
  gap: var(--rv-space-2);
}

/* Button-primitive overrides: pill + card option shapes. Sized like a real
   button (the primitive's sm is tag-sized), so it reads as something to click. */
.ChoicePill {
  height: auto;
  min-height: var(--rv-space-10);
  padding: var(--rv-space-2) var(--rv-space-4);
  border-radius: var(--rv-radius-full);
  font-size: var(--rv-text-sm);
  font-weight: var(--rv-w-medium);
  color: var(--rv-fg-1);
  white-space: normal;
}

.ChoiceCard {
  flex: 1 1 9rem;
  height: auto;
  min-height: 0;
  padding: var(--rv-space-3);
  border-radius: var(--rv-radius-lg);
  text-align: left;
  white-space: normal;
}

/* A ticked option of a multiple-choice picker. */
.ChoiceTicked {
  border-color: var(--rv-fg-2);
  background: var(--rv-bg-app-subtle);
}

.ChoiceContinue {
  display: flex;
  margin-top: var(--rv-space-3);
}

.ChoiceCardText {
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);
}

.ChoiceCardLabel {
  display: inline-flex;
  align-items: center;
  gap: var(--rv-space-1);
  font-size: var(--rv-text-sm);
  font-weight: var(--rv-w-semibold);
  color: var(--rv-fg-1);
}

.ChoiceCardDesc {
  font-size: var(--rv-text-xs);
  line-height: var(--line-height-md);
  color: var(--rv-fg-3);
}

.ChoicePicked {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--rv-space-2);
  font-size: var(--rv-text-sm);
  color: var(--rv-fg-2);
}

.ChoicePickedTag {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xxs);
  padding: var(--space-xxs) var(--rv-space-2);
  border-radius: var(--rv-radius-sm);
  background: var(--rv-accent-glow-12);
  color: var(--content-foundation-accent-a);
  font-weight: var(--rv-w-semibold);
}

.ChoicePickedTag svg {
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
}

/* ---------------- plan picker ---------------- */

.Plans {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-3);
}

.PlansTitle {
  font-family: var(--rv-font-display);
  font-weight: var(--rv-w-medium);
  font-size: var(--rv-text-md);
  color: var(--rv-fg-1);
}

.PlanGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
  gap: var(--rv-space-3);
}

.Plan {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-2);
  padding: var(--rv-space-4);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-xl);
  background: var(--rv-surface);
}

.PlanRecommended {
  border-color: var(--rv-fg-2);
  box-shadow: var(--rv-shadow-sm);
}

.PlanSoon {
  background: var(--rv-bg-app-subtle);
}

.PlanHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--rv-space-2);
}

.PlanName {
  font-weight: var(--rv-w-semibold);
  font-size: var(--rv-text-sm);
  color: var(--rv-fg-1);
}

.PlanPrice {
  font-family: var(--rv-font-display);
  font-size: var(--rv-text-lg);
  color: var(--rv-fg-1);
}

.PlanIncludes {
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-3);
}

.PlanTags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--rv-space-2);
}

.PlanReason,
.PlansNote {
  margin: 0;
  font-size: var(--rv-text-xs);
  line-height: var(--line-height-lg);
  color: var(--rv-fg-2);
}

/* ---------------- price card ---------------- */

.PriceLines {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-2);
  font-size: var(--rv-text-sm);
}

.PriceLine {
  display: flex;
  justify-content: space-between;
  gap: var(--rv-space-3);
  color: var(--rv-fg-1);
}

.PriceRate {
  color: var(--rv-fg-3);
}

.PriceTotal {
  padding-top: var(--rv-space-2);
  border-top: 1px solid var(--rv-border);
  font-weight: var(--rv-w-semibold);
}

.PriceAmount {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* Onboarding tour card: the steps, and what the tour made once it is over. */
.TourSteps {
  display: flex;
  flex-direction: column;
  gap: var(--rv-space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.TourStep {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--rv-space-2);
  font-size: var(--rv-text-sm);
  color: var(--rv-fg-2);
}

.TourStepCurrent {
  font-weight: var(--rv-w-semibold);
  color: var(--rv-fg-1);
}

.TourMade {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--rv-space-2);
  margin-top: var(--rv-space-3);
  font-size: var(--rv-text-sm);
}

.TourMadeLabel {
  width: 100%;
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-3);
}

.ChoicePill svg,
.ChoiceCardLabel svg {
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
}
